import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Users, GraduationCap, Calendar, DollarSign, TrendingUp, Clock } from 'lucide-react';
import { getAdminStats } from '../lib/api';
import { toast } from 'sonner';

const statsConfig = [
  {
    key: 'totalTeachers',
    label: 'Total Teachers',
    icon: Users,
    color: 'from-indigo-500 to-purple-600',
    bgColor: 'bg-indigo-50 dark:bg-indigo-500/10',
    textColor: 'text-indigo-600 dark:text-indigo-400',
    link: '/teachers'
  },
  {
    key: 'totalStudents',
    label: 'Total Students',
    icon: GraduationCap,
    color: 'from-emerald-500 to-teal-600',
    bgColor: 'bg-emerald-50 dark:bg-emerald-500/10',
    textColor: 'text-emerald-600 dark:text-emerald-400',
    link: '/students'
  },
  {
    key: 'totalBookings',
    label: 'Total Bookings',
    icon: Calendar,
    color: 'from-amber-500 to-orange-600',
    bgColor: 'bg-amber-50 dark:bg-amber-500/10',
    textColor: 'text-amber-600 dark:text-amber-400',
    link: '/bookings'
  },
  {
    key: 'totalRevenue',
    label: 'Total Revenue',
    icon: DollarSign,
    color: 'from-rose-500 to-pink-600',
    bgColor: 'bg-rose-50 dark:bg-rose-500/10',
    textColor: 'text-rose-600 dark:text-rose-400',
    prefix: 'R',
    link: '/bookings'
  },
  {
    key: 'pendingBookings',
    label: 'Pending Bookings',
    icon: Clock,
    color: 'from-cyan-500 to-blue-600',
    bgColor: 'bg-cyan-50 dark:bg-cyan-500/10',
    textColor: 'text-cyan-600 dark:text-cyan-400',
    link: '/bookings'
  },
  {
    key: 'completedBookings',
    label: 'Completed Bookings',
    icon: TrendingUp,
    color: 'from-green-500 to-emerald-600',
    bgColor: 'bg-green-50 dark:bg-green-500/10',
    textColor: 'text-green-600 dark:text-green-400',
    link: '/bookings'
  },
];

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await getAdminStats();
        setStats(response.data);
      } catch (error) {
        console.error('Error fetching stats:', error);
        toast.error('Failed to load dashboard statistics');
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  return (
    <div data-testid="dashboard-page" className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-white">
          Dashboard
        </h1>
        <p className="mt-1 text-slate-500 dark:text-slate-400">
          Welcome to Find Teacher Admin Panel. Here's an overview of your platform.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {statsConfig.map((stat) => (
          <Card
            key={stat.key}
            data-testid={`stat-card-${stat.key}`}
            onClick={() => navigate(stat.link)}
            className="cursor-pointer group hover:shadow-lg hover:shadow-slate-200/50 dark:hover:shadow-slate-900/50 transition-all duration-300 border-slate-200/60 dark:border-slate-700/60 bg-white dark:bg-slate-900"
          >
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-500 dark:text-slate-400">
                {stat.label}
              </CardTitle>
              <div className={`w-10 h-10 rounded-xl ${stat.bgColor} flex items-center justify-center transition-transform group-hover:scale-110`}>
                <stat.icon className={`w-5 h-5 ${stat.textColor}`} />
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="h-9 w-24" />
              ) : (
                <div className="text-3xl font-bold tracking-tight text-slate-900 dark:text-white">
                  {stat.prefix || ''}{stats?.[stat.key]?.toLocaleString() || 0}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="border-slate-200/60 dark:border-slate-700/60 bg-white dark:bg-slate-900">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-900 dark:text-white">
              Quick Actions
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-4">
            <button
              data-testid="quick-action-teachers"
              onClick={() => navigate('/teachers')}
              className="p-4 rounded-xl bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-500/20 transition-colors text-left"
            >
              <Users className="w-6 h-6 mb-2" />
              <span className="font-medium">Manage Teachers</span>
            </button>
            <button
              data-testid="quick-action-students"
              onClick={() => navigate('/students')}
              className="p-4 rounded-xl bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-500/20 transition-colors text-left"
            >
              <GraduationCap className="w-6 h-6 mb-2" />
              <span className="font-medium">Manage Students</span>
            </button>
            <button
              data-testid="quick-action-bookings"
              onClick={() => navigate('/bookings')}
              className="p-4 rounded-xl bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-500/20 transition-colors text-left"
            >
              <Calendar className="w-6 h-6 mb-2" />
              <span className="font-medium">View Bookings</span>
            </button>
            <button
              data-testid="quick-action-stats"
              className="p-4 rounded-xl bg-rose-50 dark:bg-rose-500/10 text-rose-600 dark:text-rose-400 hover:bg-rose-100 dark:hover:bg-rose-500/20 transition-colors text-left"
            >
              <TrendingUp className="w-6 h-6 mb-2" />
              <span className="font-medium">View Analytics</span>
            </button>
          </CardContent>
        </Card>

        <Card className="border-slate-200/60 dark:border-slate-700/60 bg-white dark:bg-slate-900">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-900 dark:text-white">
              Platform Overview
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between py-2 border-b border-slate-100 dark:border-slate-800">
              <span className="text-slate-500 dark:text-slate-400">Active Teachers</span>
              <span className="font-semibold text-slate-900 dark:text-white">
                {loading ? <Skeleton className="h-5 w-12" /> : stats?.totalTeachers || 0}
              </span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-slate-100 dark:border-slate-800">
              <span className="text-slate-500 dark:text-slate-400">Active Students</span>
              <span className="font-semibold text-slate-900 dark:text-white">
                {loading ? <Skeleton className="h-5 w-12" /> : stats?.totalStudents || 0}
              </span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-slate-100 dark:border-slate-800">
              <span className="text-slate-500 dark:text-slate-400">Completion Rate</span>
              <span className="font-semibold text-green-600 dark:text-green-400">
                {loading ? (
                  <Skeleton className="h-5 w-12" />
                ) : (
                  `${stats?.totalBookings > 0 ? Math.round((stats?.completedBookings / stats?.totalBookings) * 100) : 0}%`
                )}
              </span>
            </div>
            <div className="flex items-center justify-between py-2">
              <span className="text-slate-500 dark:text-slate-400">Avg. Revenue</span>
              <span className="font-semibold text-slate-900 dark:text-white">
                {loading ? (
                  <Skeleton className="h-5 w-12" />
                ) : (
                  `R${stats?.totalBookings > 0 ? Math.round(stats?.totalRevenue / stats?.totalBookings) : 0}`
                )}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
